(() => {
    'use strict';

    const MODAL_ID = 'SearchModal';
    const TOGGLE_SELECTOR = '[data-search-modal-toggle]';
    const MINIMUM_CHARACTERS = 3;
    const DEBOUNCE_DELAY = 220;
    const PREDICTIVE_LIMIT = 8;

    let searchModalInstance = null;

    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const getFocusableElements = (container) => {
        if (!container) {
            return [];
        }

        return [
            ...container.querySelectorAll(
                focusableSelector
            )
        ].filter((element) => {
            if (
                element.hasAttribute('inert') ||
                element.closest('[hidden]')
            ) {
                return false;
            }

            return Boolean(
                element.offsetWidth ||
                element.offsetHeight ||
                element.getClientRects().length
            );
        });
    };

    const parseTime = (value) => {
        const trimmed = value.trim();

        if (trimmed.endsWith('ms')) {
            return Number.parseFloat(trimmed) || 0;
        }

        if (trimmed.endsWith('s')) {
            return (
                (Number.parseFloat(trimmed) || 0) *
                1000
            );
        }

        return 0;
    };

    const getTransitionDuration = (element) => {
        if (!element) {
            return 0;
        }

        const styles =
            window.getComputedStyle(element);

        const durations =
            styles.transitionDuration
                .split(',')
                .map(parseTime);

        const delays =
            styles.transitionDelay
                .split(',')
                .map(parseTime);

        const count = Math.max(
            durations.length,
            delays.length
        );

        let longest = 0;

        for (
            let index = 0;
            index < count;
            index += 1
        ) {
            const duration =
                durations[
                index % durations.length
                ] || 0;

            const delay =
                delays[
                index % delays.length
                ] || 0;

            longest = Math.max(
                longest,
                duration + delay
            );
        }

        return longest;
    };

    class SearchModal {
        constructor() {
            this.root =
                document.getElementById(
                    MODAL_ID
                );

            if (!this.root) {
                return;
            }

            this.box =
                this.root.querySelector(
                    '[data-search-modal-box]'
                );

            this.backdrop =
                this.root.querySelector(
                    '[data-search-modal-backdrop]'
                );

            this.closeButton =
                this.root.querySelector(
                    '[data-search-modal-close]'
                );

            this.form =
                this.root.querySelector(
                    '[data-search-modal-form]'
                );

            this.input =
                this.root.querySelector(
                    '[data-search-modal-input]'
                );

            this.results =
                this.root.querySelector(
                    '[data-search-modal-results]'
                );

            this.resultsContainer =
                this.root.querySelector(
                    '[data-predictive-search-results]'
                );

            this.viewAllLink =
                this.root.querySelector(
                    '[data-search-modal-view-all]'
                );

            this.abortController =
                new AbortController();

            this.activeRequestController =
                null;

            this.debounceTimer = null;
            this.closeTimer = null;

            this.requestSequence = 0;

            this.lastFocusedElement = null;

            this.inertElements = [];

            this.initialPrompt =
                this.resultsContainer
                    ?.textContent
                    ?.trim() ||
                this.root.dataset.searchPrompt ||
                '';

            this.init();
        }

        /* ==========================================================
           Initialization
           ========================================================== */

        init() {
            if (!this.root) {
                return;
            }

            const signal =
                this.abortController.signal;

            document.addEventListener(
                'click',
                (event) => {
                    this.handleDocumentClick(
                        event
                    );
                },
                { signal }
            );

            this.backdrop?.addEventListener(
                'click',
                () => {
                    this.close();
                },
                { signal }
            );

            this.closeButton?.addEventListener(
                'click',
                () => {
                    this.close();
                },
                { signal }
            );

            this.input?.addEventListener(
                'input',
                (event) => {
                    this.handleInput(event);
                },
                { signal }
            );

            document.addEventListener(
                'keydown',
                (event) => {
                    this.handleKeydown(event);
                },
                { signal }
            );

            this.prepareTriggers();
        }

        /* ==========================================================
           Shopify routes
           ========================================================== */

        getShopRoot() {
            const root =
                window.Shopify
                    ?.routes
                    ?.root ||
                '/';

            return root.endsWith('/')
                ? root
                : `${root}/`;
        }

        getSearchUrl() {
            const configuredUrl =
                window.Shopify
                    ?.routes
                    ?.search_url;

            if (configuredUrl) {
                return new URL(
                    configuredUrl,
                    window.location.origin
                );
            }

            return new URL(
                `${this.getShopRoot()}search`,
                window.location.origin
            );
        }

        /* ==========================================================
           Trigger handling
           ========================================================== */

        prepareTriggers() {
            document
                .querySelectorAll(
                    TOGGLE_SELECTOR
                )
                .forEach((trigger) => {
                    trigger.setAttribute(
                        'aria-controls',
                        MODAL_ID
                    );

                    trigger.setAttribute(
                        'aria-expanded',
                        String(this.isOpen())
                    );
                });
        }

        handleDocumentClick(event) {
            const trigger =
                event.target.closest(
                    TOGGLE_SELECTOR
                );

            if (!trigger) {
                return;
            }

            event.preventDefault();

            if (this.isOpen()) {
                this.close();

                return;
            }

            this.open(trigger);
        }

        setTriggerExpanded(expanded) {
            document
                .querySelectorAll(
                    TOGGLE_SELECTOR
                )
                .forEach((trigger) => {
                    trigger.setAttribute(
                        'aria-expanded',
                        String(expanded)
                    );
                });
        }

        /* ==========================================================
           Modal state
           ========================================================== */

        isOpen() {
            return Boolean(
                this.root?.classList.contains(
                    'is-open'
                )
            );
        }

        closeOtherOverlays() {
            /*
             * Close an existing cart drawer before Search opens.
             *
             * This intentionally uses its public DOM control rather than
             * depending on the old Veyra JavaScript namespace.
             */
            const cartDrawer =
                document.querySelector(
                    '[data-cart-drawer]'
                );

            if (
                cartDrawer?.classList.contains(
                    'is-open'
                )
            ) {
                const cartClose =
                    cartDrawer.querySelector(
                        '[data-cart-drawer-close]'
                    );

                cartClose?.click();
            }

            /*
             * Close the mobile navigation drawer if it is currently open.
             */
            const mobileHeaderMenu =
                document.querySelector(
                    '[data-header-mobile-menu].is-open'
                );

            if (mobileHeaderMenu) {
                const headerClose =
                    mobileHeaderMenu.querySelector(
                        '.header__mobile-close'
                    );

                headerClose?.click();
            }

            /*
             * Gives future components one neutral coordination hook.
             */
            document.dispatchEvent(
                new CustomEvent(
                    'theme:overlay:open',
                    {
                        detail: {
                            source: 'search'
                        }
                    }
                )
            );
        }

        open(trigger = null) {
            if (
                !this.root ||
                this.isOpen()
            ) {
                return;
            }

            this.closeOtherOverlays();

            window.clearTimeout(
                this.closeTimer
            );

            this.lastFocusedElement =
                trigger instanceof HTMLElement
                    ? trigger
                    : (
                        document.activeElement instanceof
                            HTMLElement
                            ? document.activeElement
                            : null
                    );

            this.root.hidden = false;

            this.setTriggerExpanded(true);

            this.setBackgroundInert(true);

            requestAnimationFrame(() => {
                this.root.classList.add(
                    'is-open'
                );

                const reducedMotion =
                    window.matchMedia(
                        '(prefers-reduced-motion: reduce)'
                    ).matches;

                window.setTimeout(
                    () => {
                        this.input?.focus({
                            preventScroll: true
                        });
                    },
                    reducedMotion
                        ? 0
                        : 70
                );
            });

            document.dispatchEvent(
                new CustomEvent(
                    'theme:search:open'
                )
            );
        }

        close({
            restoreFocus = true,
            immediate = false
        } = {}) {
            if (
                !this.root ||
                !this.isOpen()
            ) {
                return;
            }

            this.abortPendingRequest();

            window.clearTimeout(
                this.debounceTimer
            );

            window.clearTimeout(
                this.closeTimer
            );

            this.root.classList.remove(
                'is-open'
            );

            this.setTriggerExpanded(false);

            const reducedMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches;

            if (
                immediate ||
                reducedMotion
            ) {
                this.finishClose(
                    restoreFocus
                );

                return;
            }

            const duration =
                Math.max(
                    getTransitionDuration(
                        this.box
                    ),
                    getTransitionDuration(
                        this.backdrop
                    )
                );

            this.closeTimer =
                window.setTimeout(
                    () => {
                        this.finishClose(
                            restoreFocus
                        );
                    },
                    duration + 20
                );
        }

        finishClose(
            restoreFocus = true
        ) {
            if (!this.root) {
                return;
            }

            this.root.hidden = true;

            this.setBackgroundInert(false);

            if (
                restoreFocus &&
                this.lastFocusedElement &&
                this.lastFocusedElement.isConnected &&
                typeof this.lastFocusedElement
                    .focus === 'function'
            ) {
                this.lastFocusedElement.focus();
            }

            this.lastFocusedElement = null;

            document.dispatchEvent(
                new CustomEvent(
                    'theme:search:close'
                )
            );
        }

        /* ==========================================================
           Background inert handling
           ========================================================== */

        setBackgroundInert(
            shouldInert
        ) {
            if (
                !(
                    'inert' in
                    HTMLElement.prototype
                )
            ) {
                return;
            }

            if (shouldInert) {
                this.inertElements = [];

                const owner =
                    Array.from(
                        document.body.children
                    ).find((child) =>
                        child.contains(this.root)
                    );

                Array.from(
                    document.body.children
                ).forEach((child) => {
                    if (
                        child === owner ||
                        child.inert
                    ) {
                        return;
                    }

                    this.inertElements.push({
                        element: child,
                        previous: child.inert
                    });

                    child.inert = true;
                });

                return;
            }

            this.inertElements.forEach(
                ({
                    element,
                    previous
                }) => {
                    if (element.isConnected) {
                        element.inert =
                            previous;
                    }
                }
            );

            this.inertElements = [];
        }

        /* ==========================================================
           Search input
           ========================================================== */

        handleInput(event) {
            const query =
                event.target.value.trim();

            window.clearTimeout(
                this.debounceTimer
            );

            this.abortPendingRequest();

            if (
                query.length <
                MINIMUM_CHARACTERS
            ) {
                this.showPrompt();

                return;
            }

            this.showLoading();

            this.debounceTimer =
                window.setTimeout(
                    () => {
                        this.fetchResults(
                            query
                        );
                    },
                    DEBOUNCE_DELAY
                );
        }

        /* ==========================================================
           Predictive search
           ========================================================== */

        async fetchResults(query) {
            this.abortPendingRequest();

            const controller =
                new AbortController();

            this.activeRequestController =
                controller;

            const requestId =
                ++this.requestSequence;

            const url =
                new URL(
                    `${this.getShopRoot()}search/suggest`,
                    window.location.origin
                );

            url.searchParams.set(
                'q',
                query
            );

            url.searchParams.set(
                'resources[type]',
                'product'
            );

            url.searchParams.set(
                'resources[limit]',
                String(PREDICTIVE_LIMIT)
            );

            url.searchParams.set(
                'section_id',
                'predictive-search'
            );

            this.setBusy(true);

            try {
                const response =
                    await fetch(
                        url.toString(),
                        {
                            method: 'GET',

                            credentials:
                                'same-origin',

                            headers: {
                                Accept: 'text/html'
                            },

                            signal:
                                controller.signal
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        `Predictive search failed: ${response.status}`
                    );
                }

                const html =
                    await response.text();

                if (
                    requestId !==
                    this.requestSequence
                ) {
                    return;
                }

                const documentFragment =
                    new DOMParser()
                        .parseFromString(
                            html,
                            'text/html'
                        );

                const content =
                    documentFragment
                        .querySelector(
                            '[data-predictive-search-content]'
                        );

                if (
                    !content ||
                    !this.resultsContainer
                ) {
                    this.showError();

                    return;
                }

                this.resultsContainer
                    .replaceChildren(
                        content
                    );

                this.updateViewAllLink(
                    query
                );
            } catch (error) {
                if (
                    error.name ===
                    'AbortError'
                ) {
                    return;
                }

                console.error(
                    '[Theme Search]',
                    error
                );

                this.showError();
            } finally {
                if (
                    requestId ===
                    this.requestSequence
                ) {
                    this.setBusy(false);

                    this.activeRequestController =
                        null;
                }
            }
        }

        abortPendingRequest() {
            if (
                this.activeRequestController
            ) {
                this.activeRequestController
                    .abort();

                this.activeRequestController =
                    null;
            }

            this.requestSequence += 1;
        }

        /* ==========================================================
           View all
           ========================================================== */

        updateViewAllLink(query) {
            if (!this.viewAllLink) {
                return;
            }

            const url =
                this.getSearchUrl();

            url.searchParams.set(
                'q',
                query
            );

            this.viewAllLink.href =
                `${url.pathname}${url.search}`;

            this.viewAllLink.hidden =
                false;
        }

        /* ==========================================================
           Result states
           ========================================================== */

        setBusy(isBusy) {
            this.root?.classList.toggle(
                'is-loading',
                isBusy
            );

            this.results?.setAttribute(
                'aria-busy',
                String(isBusy)
            );
        }

        clearResults() {
            if (
                !this.resultsContainer
            ) {
                return false;
            }

            this.resultsContainer
                .replaceChildren();

            return true;
        }

        hideViewAll() {
            if (this.viewAllLink) {
                this.viewAllLink.hidden =
                    true;
            }
        }

        showPrompt() {
            if (!this.clearResults()) {
                return;
            }

            const prompt =
                document.createElement(
                    'p'
                );

            prompt.className =
                'search-modal__prompt';

            prompt.textContent =
                this.initialPrompt;

            this.resultsContainer
                .appendChild(prompt);

            this.hideViewAll();

            this.setBusy(false);
        }

        showLoading() {
            if (!this.clearResults()) {
                return;
            }

            const loading =
                document.createElement(
                    'p'
                );

            loading.className =
                'search-modal__loading';

            loading.setAttribute(
                'role',
                'status'
            );

            loading.textContent =
                this.root?.dataset.searchLoading || '';

            this.resultsContainer
                .appendChild(loading);

            this.hideViewAll();

            this.setBusy(true);
        }

        showError() {
            if (!this.clearResults()) {
                return;
            }

            const error =
                document.createElement(
                    'p'
                );

            error.className =
                'search-modal__error';

            error.setAttribute(
                'role',
                'status'
            );

            error.textContent =
                this.root?.dataset.searchError || '';

            this.resultsContainer
                .appendChild(error);

            this.hideViewAll();

            this.setBusy(false);
        }

        /* ==========================================================
           Keyboard
           ========================================================== */

        handleKeydown(event) {
            if (!this.isOpen()) {
                return;
            }

            if (
                event.key ===
                'Escape'
            ) {
                event.preventDefault();

                this.close();

                return;
            }

            if (
                event.key !==
                'Tab'
            ) {
                return;
            }

            this.trapFocus(event);
        }

        trapFocus(event) {
            if (!this.box) {
                return;
            }

            const focusable =
                getFocusableElements(
                    this.box
                );

            if (!focusable.length) {
                event.preventDefault();

                this.box.focus();

                return;
            }

            const first =
                focusable[0];

            const last =
                focusable[
                focusable.length - 1
                ];

            if (
                !this.box.contains(
                    document.activeElement
                )
            ) {
                event.preventDefault();

                first.focus();

                return;
            }

            if (
                event.shiftKey &&
                document.activeElement ===
                first
            ) {
                event.preventDefault();

                last.focus();

                return;
            }

            if (
                !event.shiftKey &&
                document.activeElement ===
                last
            ) {
                event.preventDefault();

                first.focus();
            }
        }

        /* ==========================================================
           Cleanup
           ========================================================== */

        destroy() {
            window.clearTimeout(
                this.debounceTimer
            );

            window.clearTimeout(
                this.closeTimer
            );

            this.activeRequestController
                ?.abort();

            this.abortController.abort();

            this.setBackgroundInert(false);

            this.root?.classList.remove(
                'is-open'
            );

            if (this.root) {
                this.root.hidden = true;
            }
        }
    }

    /* ============================================================
       Initialization
       ============================================================ */

    const initializeSearchModal = () => {
        searchModalInstance?.destroy();

        const instance =
            new SearchModal();

        if (!instance.root) {
            searchModalInstance = null;

            return;
        }

        searchModalInstance =
            instance;
    };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            initializeSearchModal,
            {
                once: true
            }
        );
    } else {
        initializeSearchModal();
    }

    /* ============================================================
       Shopify Theme Editor
       ============================================================ */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            if (
                event.target
                    .querySelector?.(
                        '[data-search-modal]'
                    )
            ) {
                initializeSearchModal();
            }
        }
    );
})();